// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/izgara/izgara-cekirdek.ts + izgara-tampon.ts + izgara-select-open.ts
// Fark: tek kanal (`onRowsChange`) ve genel `GridColumn<R>`; veriyi tampona almaz, kontrollüdür
// (son yayımlanan dizi `rowsRef`'te); pano gerçek copy/cut/paste olaylarıyla; fill/taşıma/menü tutamağı yok.
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'

import { createHistory } from './history'
import { issueCells, nextIssue } from './issues'
import { mapGridKey, type GridKeyActions } from './keys'
import {
  areaSelection,
  copyRange,
  isEditable,
  planClear,
  planFillDown,
  planPaste,
  resolveInput,
  type WritePlan,
} from './plan'
import {
  atTabEdge,
  cellKey,
  clampCoord,
  clampRange,
  columnRange,
  fullRange,
  homeEndTarget,
  moveTarget,
  rangeOf,
  rowRange,
  tabTarget,
} from './selection'
import type { Coord, EditableGridProps, EditorNav, Range } from './types'

/** After a list pick, pointer opens are ignored for this long (ghost click). */
export const GHOST_CLICK_MS = 400
/** How long a written cell keeps its highlight. */
const FLASH_MS = 700

interface Snapshot<R> {
  rows: R[]
  area: Range
}

export interface Editing {
  row: number
  col: number
  /** Typed character that started the editor (type-to-replace), if any. */
  seed?: string
}

/** Wrapped so the render-purity lint does not see a direct impure call in the hook body. */
const now = () => performance.now()

const single = (c: Coord) => ({ anchor: c, extent: c })

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  )
}

export function useGridCore<R>(props: EditableGridProps<R>) {
  const {
    rows,
    columns,
    rowKey,
    onRowsChange,
    newRow,
    issues,
    historyKey,
    readOnly = false,
  } = props
  const rowCount = rows.length
  const colCount = columns.length

  const uid = useId()
  const tableRef = useRef<HTMLTableElement>(null)
  const rowsRef = useRef(rows)
  useLayoutEffect(() => {
    rowsRef.current = rows
  })

  const [sel, setSel] = useState<{ anchor: Coord; extent: Coord } | null>(null)
  const [editing, setEditing] = useState<Editing | null>(null)
  const [written, setWritten] = useState<ReadonlySet<string>>(() => new Set())
  const [history] = useState(() => createHistory<Snapshot<R>>())
  const dragging = useRef(false)
  const pickedAt = useRef(Number.NEGATIVE_INFINITY)
  const flashTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    history.clear()
  }, [historyKey, history])

  useEffect(() => {
    const stop = () => {
      dragging.current = false
    }
    window.addEventListener('mouseup', stop)
    return () => {
      window.removeEventListener('mouseup', stop)
      window.clearTimeout(flashTimer.current)
    }
  }, [])

  const anchor =
    sel && rowCount > 0 && colCount > 0 ? clampCoord(sel.anchor, rowCount, colCount) : null
  const extent = sel && anchor ? clampCoord(sel.extent, rowCount, colCount) : null
  const range = anchor && extent ? rangeOf(anchor, extent) : null

  const cellId = (r: number, c: number) => `${uid}-r${r}-c${c}`

  // Keep the moving end of the selection in view (header / gutter are sticky: see scroll-margin).
  const extentRow = extent?.row
  const extentCol = extent?.col
  useLayoutEffect(() => {
    if (extentRow === undefined || extentCol === undefined) return
    document.getElementById(cellId(extentRow, extentCol))?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extentRow, extentCol])

  function focusGrid() {
    tableRef.current?.focus({ preventScroll: true })
  }

  function flash(cells: Coord[], next: R[]) {
    if (prefersReducedMotion() || cells.length === 0) return
    const keys = new Set<string>()
    for (const c of cells) {
      const row = next[c.row]
      const col = columns[c.col]
      if (row !== undefined && col) keys.add(cellKey(rowKey(row), col.id))
    }
    setWritten(keys)
    window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setWritten(new Set()), FLASH_MS)
  }

  /** The one write gate: records the undo entry, emits the whole new array, flashes the cells. */
  function apply(next: R[], area: Range, cells: Coord[]) {
    history.push({ rows: rowsRef.current, area })
    rowsRef.current = next
    onRowsChange(next)
    flash(cells, next)
  }

  function applyPlan(plan: WritePlan<R> | null, select = false) {
    if (!plan) return
    apply(plan.rows, plan.area, plan.written)
    if (select) setSel(areaSelection(plan.area))
  }

  function restore(state: Snapshot<R> | undefined) {
    if (!state) return
    rowsRef.current = state.rows
    onRowsChange(state.rows)
    const area = clampRange(state.area, state.rows.length, colCount)
    setSel(areaSelection(area))
    setEditing(null)
  }

  const undo = () => {
    if (readOnly) return
    restore(history.undo((popped) => ({ rows: rowsRef.current, area: popped.area })))
  }
  const redo = () => {
    if (readOnly) return
    restore(history.redo((popped) => ({ rows: rowsRef.current, area: popped.area })))
  }

  // ── Editor ───────────────────────────────────────────────────────────────────

  function startEdit(c: Coord, seed?: string): boolean {
    const row = rowsRef.current[c.row]
    const col = columns[c.col]
    if (!row || !col || !isEditable(col, row, readOnly)) return false
    setEditing({ row: c.row, col: c.col, seed })
    return true
  }

  /**
   * Closes the editor. `text` null = cancel. `refocus` false when the editor lost focus on its own
   * (a click elsewhere): the grid must not steal the focus back.
   */
  function closeEditor(text: string | null, nav: EditorNav, refocus = true) {
    const ed = editing
    if (!ed) return
    const cur = rowsRef.current
    const col = columns[ed.col]
    const row = cur[ed.row]
    let next = cur
    let changed = false
    if (text !== null && col && row !== undefined) {
      const resolved = resolveInput(col, row, text)
      if (resolved !== col.getText(row)) {
        next = cur.slice()
        next[ed.row] = col.setText(row, resolved)
        changed = true
      }
    }

    let target: Coord = { row: ed.row, col: ed.col }
    if (nav !== 'none') {
      const wanted =
        nav === 'down' || nav === 'up'
          ? { row: ed.row + (nav === 'down' ? 1 : -1), col: ed.col }
          : tabTarget(target, nav === 'prev', colCount)
      if (wanted.row >= cur.length) {
        // Past the last row: only an actual edit grows the grid (an idle Enter does not).
        if (changed && newRow) {
          next = next.slice()
          next.push(newRow())
          target = wanted
        }
      } else if (wanted.row >= 0) {
        target = wanted
      }
    }

    if (next !== cur) apply(next, rangeOf(ed, ed), changed ? [{ row: ed.row, col: ed.col }] : [])
    setEditing(null)
    if (nav !== 'none') setSel(single(target))
    if (refocus) focusGrid()
  }

  const markPicked = () => {
    pickedAt.current = now()
  }
  const mayOpenFromPointer = () => now() - pickedAt.current >= GHOST_CLICK_MS

  // ── Pointer ──────────────────────────────────────────────────────────────────

  function onCellMouseDown(e: MouseEvent<HTMLElement>, row: number, col: number) {
    if (e.button !== 0) return
    if (editing && editing.row === row && editing.col === col) return // the editor owns this click
    e.preventDefault()
    focusGrid() // synchronous (beltasapp trap 3): blurs an open editor, which commits
    const target = { row, col }
    if (e.shiftKey && sel) setSel({ anchor: sel.anchor, extent: target })
    else setSel(single(target))
    dragging.current = true
  }

  function onCellMouseEnter(row: number, col: number) {
    if (!dragging.current) return
    setSel((s) => (s ? { anchor: s.anchor, extent: { row, col } } : s))
  }

  function onCellDoubleClick(row: number, col: number) {
    if (mayOpenFromPointer()) startEdit({ row, col })
  }

  /** The ▾ chevron of a select cell: one click opens the list. */
  function openListAt(row: number, col: number) {
    if (!mayOpenFromPointer()) return
    setSel(single({ row, col }))
    startEdit({ row, col })
  }

  function selectRowAt(row: number) {
    focusGrid()
    const r = rowRange(row, colCount)
    setSel(areaSelection(r))
  }

  function selectColumnAt(col: number) {
    focusGrid()
    const r = columnRange(col, rowCount)
    setSel(areaSelection(r))
  }

  // ── Keyboard / clipboard ─────────────────────────────────────────────────────

  const actions: GridKeyActions = {
    move: (dRow, dCol, extend) => {
      if (!anchor || !extent) return
      const t = moveTarget(extend ? extent : anchor, dRow, dCol, rowCount, colCount)
      setSel(extend ? { anchor, extent: t } : single(t))
    },
    homeEnd: (end, ctrl, extend) => {
      if (!anchor || !extent) return
      const t = homeEndTarget(extend ? extent : anchor, end, ctrl, rowCount, colCount)
      setSel(extend ? { anchor, extent: t } : single(t))
    },
    tab: (back) => {
      if (!anchor || atTabEdge(anchor, back, rowCount, colCount)) return false
      setSel(single(tabTarget(anchor, back, colCount)))
      return true
    },
    enter: (back) => {
      if (!anchor) return
      if (!back && startEdit(anchor)) return
      setSel(single(moveTarget(anchor, back ? -1 : 1, 0, rowCount, colCount)))
    },
    edit: (seed) => {
      if (anchor) startEdit(anchor, seed)
    },
    openList: () => {
      if (!anchor || columns[anchor.col]?.kind !== 'select') return false
      return startEdit(anchor)
    },
    fillDown: () => {
      if (range) applyPlan(planFillDown(rowsRef.current, columns, range, readOnly))
    },
    clear: () => {
      if (range) applyPlan(planClear(rowsRef.current, columns, range, readOnly))
    },
    collapse: () => setSel((s) => (s ? single(s.anchor) : s)),
    selectAll: () => {
      if (rowCount === 0 || colCount === 0) return
      setSel(areaSelection(fullRange(rowCount, colCount)))
    },
    undo,
    redo,
    issue: (back) => {
      const target = nextIssue(issueCells(rowsRef.current, columns, rowKey, issues), anchor, back)
      if (target) setSel(single(target))
    },
  }

  const idle = (e: { target: EventTarget; currentTarget: EventTarget }) =>
    !editing && e.target === e.currentTarget

  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (!idle(e)) return
    mapGridKey(e, actions)
  }

  function onFocus(e: React.FocusEvent<HTMLElement>) {
    if (e.target !== e.currentTarget || sel || rowCount === 0 || colCount === 0) return
    setSel(single({ row: 0, col: 0 }))
  }

  function onCopy(e: ClipboardEvent<HTMLElement>) {
    if (!idle(e) || !range) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', copyRange(rowsRef.current, columns, range))
  }

  function onCut(e: ClipboardEvent<HTMLElement>) {
    if (!idle(e) || !range) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', copyRange(rowsRef.current, columns, range))
    applyPlan(planClear(rowsRef.current, columns, range, readOnly))
  }

  function onPaste(e: ClipboardEvent<HTMLElement>) {
    if (!idle(e)) return
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    if (!text) return
    const at = range ?? { r0: 0, r1: 0, c0: 0, c1: 0 }
    applyPlan(
      planPaste({ rows: rowsRef.current, columns, text, range: at, newRow, readOnly }),
      true,
    )
  }

  const view = {
    anchor,
    extent,
    range,
    editing,
    written,
    cellId,
    activeId: anchor ? cellId(anchor.row, anchor.col) : undefined,
    closeEditor,
    markPicked,
    handlers: { onKeyDown, onFocus, onCopy, onCut, onPaste },
    cell: { onCellMouseDown, onCellMouseEnter, onCellDoubleClick, openListAt },
    selectRowAt,
    selectColumnAt,
  }
  return [tableRef, view] as const
}
